import { ChangeEvent, FormEvent, useState } from "react";
import { ActionByType } from "../../common/ProfileDocument";
import { TrainingDocument } from "../../common/TrainingDocument";
import { useUploadTrainingDocumentMutation } from "../../redux/api/trainingDocument";
import { toast } from "react-toastify";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Backup } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useGetTrainingsQuery } from "../../redux/api/training";

interface TrainingDocumentUploadModalProps {
  profileId: number;
  onUpload: Function;
  actionByType: ActionByType;
}

const TrainingDocumentUploadModal = (
  props: TrainingDocumentUploadModalProps
) => {
  const [trainingDocument, setTrainingDocument] = useState<TrainingDocument>({
    id: 0,
    fileUrl: "",
    fileKey: "",
    contentType: "",
    issueDate: "",
    expirationDate: "",
    lifetimeValidity: false,
    displayName: "",
    training: undefined,
    actionByType: props.actionByType,
  });
  const [file, setFile] = useState<File>();
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data: trainings, isLoading, isFetching } = useGetTrainingsQuery();

  const [uploadTrainingDocument] = useUploadTrainingDocumentMutation();

  const handleOpen = () => {
    setFile(undefined);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const clearForm = () => {
    setTrainingDocument({
      id: 0,
      fileUrl: "",
      fileKey: "",
      contentType: "",
      training: undefined,
      issueDate: "",
      expirationDate: "",
      lifetimeValidity: false,
      displayName: "",
      actionByType: props.actionByType,
    });
  };

  const handleTrainingChange = (trainingId: number) => {
    const newTraining = trainings!.find(t => t.id === trainingId);
    setTrainingDocument({
      ...trainingDocument,
      training: newTraining,
    });
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const mb = 1024 * 1024; // size of a MB in Bytes
    if (!event.target.files || !event.target.files[0]) {
      setFile(undefined);
      return;
    }
    if (event.target.files[0].size > 20 * mb) {
      toast.error(<p>File is too big. Files must be 20MB or less.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    setFile(event.target.files[0]);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // check that required values are present
    if (!file) {
      toast.error(<p>No valid file to Upload.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }
    if (trainingDocument.lifetimeValidity !== (trainingDocument.expirationDate.length === 0)) {
      toast.error(<p>A document must either have lifetime validity or an expiration date.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    uploadDocument(file, convertTrainingDocumentToBody(trainingDocument));
  };

  const uploadDocument = async (file: File, body: string) => {
    setUploading(true);
    // format file and body as Form Data
    const data = new FormData();
    data.append("file", file);
    data.append("document", new Blob([body], { type: "application/json" }));
    const response = await uploadTrainingDocument(data);
    setUploading(false);
    if ("error" in response === false) {
      handleClose();
      clearForm();
      props.onUpload();
    }
  };

  const convertTrainingDocumentToBody = (document: TrainingDocument) => {
    // convert empty values to undefined
    return JSON.stringify({
      profile: {
        id: props.profileId,
      },
      training: document.training,
      issueDate: document.issueDate ? document.issueDate : undefined,
      expirationDate: document.expirationDate
        ? document.expirationDate
        : undefined,
      lifetimeValidity: document.lifetimeValidity,
    });
  };

  return (
    <>
      <Button
        className="blue-link-button"
        id="training-upload"
        onClick={handleOpen}
      >
        <Backup />
        Upload Training Document
      </Button>
      <Dialog
        className="modal"
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        sx={{
          "& .MuiDialog-paper": { maxWidth: "100% !important" },
        }}
      >
        <div className="modal-header mb-2">
          <Typography id="modal-title" variant="h3">
            Upload Document
          </Typography>
          <Button onClick={() => setOpen(false)} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <form onSubmit={handleSubmit}>
          <div
            className="form-narrow"
            id="upload-training-fields"
            style={{ textOverflow: "ellipsis" }}
          >
            {!isLoading && !isFetching && (
              <div>
                <InputLabel id="trainingLabel">Training</InputLabel>
                <Select
                  labelId="trainingLabel"
                  id="training"
                  notched={false}
                  value={trainingDocument.training}
                  label="Training"
                  onChange={(event) => {
                    handleTrainingChange(+event.target.value);
                  }}
                >
                  {trainings?.map((training) => (
                    <MenuItem key={training.name} value={training.id}>
                      {training.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}
            <label htmlFor="issueDate">
              <span>Issue Date:</span>
              <input
                type="date"
                id="issueDate"
                value={trainingDocument.issueDate}
                onChange={(event) =>
                  setTrainingDocument({
                    ...trainingDocument,
                    issueDate: event.target.value,
                  })
                }
              />
            </label>
            <div>
              <label htmlFor="expirationDate">
                <span>Expiration Date:</span>
                <input
                  type="date"
                  id="expirationDate"
                  value={trainingDocument.expirationDate}
                  onChange={(event) =>
                    setTrainingDocument({
                      ...trainingDocument,
                      expirationDate: event.target.value,
                    })
                  }
                />
              </label>
              <label htmlFor="lifetimeValidity">
                <span style={{ marginTop: "10px" }}>Lifetime Validity:</span>
                <div style={{ width: "100px" }}
                >
                  <Checkbox
                    name="lifetimeValidity"
                    checked={trainingDocument.lifetimeValidity}
                    onChange={(e) => setTrainingDocument({ ...trainingDocument, lifetimeValidity: e.target.checked ? true : false })}
                  />
                </div>
              </label>
            </div>
          </div>
          <label htmlFor="fileUpload" style={{ marginTop: "3%" }}>
            <span>Upload File:</span>
            <input type="file" id="fileUpload" onChange={handleFileChange} />
          </label>
          <div className="modal-button">
            <Button
              className="outline-button-small"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <input
              className="blue-button-small"
              type="submit"
              value="Upload Document"
              disabled={uploading}
            />
            {uploading && <CircularProgress />}
          </div>
        </form>
      </Dialog>
    </>
  );
};

export default TrainingDocumentUploadModal;
